import { screenSignal } from './screenScope';

/*
| Mobile header consolidation. On phones the screen toolbar's secondary controls
| (view switcher, filters, templates, export/Drive — marked `[data-toolbar-rest]`)
| relocate into a shared bottom sheet opened by a single "Možnosti" button
| (`[data-toolbar-more]`); the date stepper stays inline and the primary action is
| hidden (a screen-level FAB covers it). On desktop everything stays in the toolbar.
| Moving the live nodes preserves their existing handlers — no per-control rewrite.
*/
const MOBILE = '(max-width: 767px)';

export function initToolbar(): void {
    const rest = document.querySelector<HTMLElement>('#screen [data-toolbar-rest]');
    const more = document.querySelector<HTMLElement>('#screen [data-toolbar-more]');
    const sheet = document.querySelector<HTMLElement>('[data-toolbar-sheet]');
    const backdrop = document.querySelector<HTMLElement>('[data-toolbar-sheet-backdrop]');
    const body = document.querySelector<HTMLElement>('[data-toolbar-sheet-body]');
    if (!sheet || !backdrop || !body) {
        return;
    }

    // A fresh screen starts with a closed, empty sheet (drop the previous screen's
    // relocated controls, which were moved into this persistent layout-level sheet).
    closeSheet(sheet, backdrop);
    body.replaceChildren();

    if (!rest || !more) {
        return;
    }

    // Anchor the rest block's home so it can return to the toolbar on widen.
    const home = document.createComment('toolbar-rest');
    rest.before(home);

    // One persistent MediaQueryList — a fresh matchMedia() per use could be GC'd
    // before its `change` listener fires.
    const mq = window.matchMedia(MOBILE);

    const layout = (): void => {
        if (mq.matches) {
            if (rest.parentElement !== body) {
                body.appendChild(rest);
            }
        } else if (rest.parentElement === body) {
            home.after(rest);
            closeSheet(sheet, backdrop);
        }
    };

    layout();

    more.addEventListener('click', () => openSheet(sheet, backdrop), { signal: screenSignal() });
    backdrop.addEventListener('click', () => closeSheet(sheet, backdrop), { signal: screenSignal() });
    sheet.querySelector('[data-toolbar-sheet-close]')?.addEventListener('click', () => closeSheet(sheet, backdrop), { signal: screenSignal() });
    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !sheet.hidden) {
            closeSheet(sheet, backdrop);
        }
    }, { signal: screenSignal() });
    mq.addEventListener('change', layout, { signal: screenSignal() });
}

function openSheet(sheet: HTMLElement, backdrop: HTMLElement): void {
    backdrop.hidden = false;
    sheet.hidden = false;
}

function closeSheet(sheet: HTMLElement, backdrop: HTMLElement): void {
    sheet.hidden = true;
    backdrop.hidden = true;
}
